import {Tooltip} from "primereact/tooltip";

import type {ValueOrderData} from "@reva/shared/value-order";

import {EvaPlusOrderLink} from "../../component/EvaPlusOrderLink";
import {useRevaTranslation} from "../../hooks/languageHooks";
import {useRevaSelector} from "../../redux/revaHooks";
import {selectExternalValueOrders} from "../../redux/valueOrdersSelectors";
import {selectRevaOrder} from "../../redux/valueOrdersSlice";
import {formatDate} from "../../util/dateUtil";

interface LinkedOrderNumberProps {
    valueOrder: ValueOrderData | null;
}

/**
 * Sub-Header Element - Link to Eva+ (if order type is external and the license may see Eva+ links).
 */
function LinkedOrderNumber({valueOrder}: LinkedOrderNumberProps) {
    const {t, language} = useRevaTranslation();

    if (!valueOrder) return null;

    const orderNumber = valueOrder.valueExternalOrderNumber;
    const orderGuid = valueOrder.valueOrderGuid;
    const orderData = valueOrder.orderDetails;

    return (
        <>
            <span id={`header-order-element-${valueOrder.valueOrderGuid}`}>
                <span className="pi pi-tag mr-2" />
                {valueOrder.orderType === "Reva" ? (
                    <span>{orderNumber}</span>
                ) : (
                    <EvaPlusOrderLink orderGuid={orderGuid} orderNumber={orderNumber} />
                )}
            </span>
            <Tooltip target={`#header-order-element-${valueOrder.valueOrderGuid}`}>
                <div>
                    <strong>{t("order.order.orderType")}</strong> {t(`order.orderType.${valueOrder.orderType}`)}
                </div>
                <div>
                    <strong>{t("order.bank.client")}</strong> {orderData?.clientInformation?.name}
                </div>
                <div>
                    <strong>{t("order.status.creationDate")}</strong> {formatDate(orderData?.creationDate, language)}
                </div>
            </Tooltip>
        </>
    );
}

/**
 * Sub-Header Element - Order Number(s).
 * If there are "external" Value Orders: show all their Order Numbers with Link to Eva+.
 * Otherwise, show the Reva Order Number without a link.
 * Wrapped in a flex container so the spacing between several order numbers is owned by
 * the layout (see .HeaderOrderNumbers) instead of per-item margins.
 */
export default function OrderNumbersElement() {
    const externalValueOrders = useRevaSelector(selectExternalValueOrders);
    const revaValueOrder = useRevaSelector(selectRevaOrder);

    // Don't display anything, if neither value order exists
    if (!revaValueOrder && !externalValueOrders.length) return null;

    return (
        <span className="HeaderOrderNumbers">
            {externalValueOrders.length > 0 ? (
                externalValueOrders.map((valueOrder) => (
                    <LinkedOrderNumber key={valueOrder.valueOrderGuid} valueOrder={valueOrder} />
                ))
            ) : (
                <LinkedOrderNumber valueOrder={revaValueOrder} />
            )}
        </span>
    );
}
